import { getData, postData, putData } from 'src/api/http.service';
import { DeliveryPackageStatusEnum } from 'src/models';
import {
  generateUserInfoHeader,
  getAPIEndpoints,
  handleApiError,
  handleApiSuccess,
} from 'src/utils/general.utils';
import { ApiCallFailedAction, ApiCallInProgress, ApiCallSuccessAction } from './uistate.action';

export const DeliveryActionTypes = {
  GET_CURRENT_TABLE_INFO: '[API] : Get current table info',
  UPDATE_ADDRESS_DATA: '[API] : Update address data',
  PROCESS_ORDER: '[API] : Process the order',
  UPDATE_ORDER_DATA: '[API] : Update order data',
  GET_ALL_CATALOGUES_DELIVERY_DATA: '[API] : Get all catalogues data',
  BULK_ASSIGN_TRACKING: '[API] : Bulk assign tracking ids',
  RESET_BULK_ASSIGN_RESULT: '[API] : Reset bulk assign result',
  GET_COURSES_BY_STATUS: '[API] : Get courses by status',
};

/*
  Plain Actions
*/

export function GetCurrentTableInfoSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.GET_CURRENT_TABLE_INFO,
    payload,
  };
}

export function UpdateAddressSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.UPDATE_ADDRESS_DATA,
    payload,
  };
}

export function ProcessOrderSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.PROCESS_ORDER,
    payload,
  };
}

export function CancelOrderSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.PROCESS_ORDER,
    payload,
  };
}

export function UpdateOrderSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.UPDATE_ORDER_DATA,
    payload,
  };
}

export function GetAllCataloguesDeliverySuccessAction(payload) {
  return {
    type: DeliveryActionTypes.GET_ALL_CATALOGUES_DELIVERY_DATA,
    payload,
  };
}

export function BulkAssignTrackingSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.BULK_ASSIGN_TRACKING,
    payload,
  };
}

export function ResetBulkAssignResultAction() {
  return {
    type: DeliveryActionTypes.RESET_BULK_ASSIGN_RESULT,
    payload: null,
  };
}

export function GetCoursesByStatusSuccessAction(payload) {
  return {
    type: DeliveryActionTypes.GET_COURSES_BY_STATUS,
    payload,
  };
}

/*
  Async Actions
*/

export function GetDeliveriesListByStatusAction(options = {}, metaData) {
  const paginateOptions = {
    size: options?.size ?? 10,
    page: options?.page ?? 0,
    sort: 'DESC',
    orderBy: 'purchaseDate',
  };
  if (!options.status) return;
  let url =
    getAPIEndpoints().getDeliveriesByStatus +
    `/${options.status}?page=${paginateOptions.page}&size=${paginateOptions.size}&sort=${paginateOptions.sort}&orderBy=${paginateOptions.orderBy}`;
  const trimmedQuery = options.query?.trim();
  if (trimmedQuery) {
    url += `&query=${encodeURIComponent(trimmedQuery)}`;
  }
  if (options.catalogueIds?.length) {
    url += `&catalogueIds=${encodeURIComponent(options.catalogueIds.join(','))}`;
  }
  if (options.states?.length) {
    url += `&state=${encodeURIComponent(options.states.join(','))}`;
  }
  if (options.fromDate) {
    url += `&startDate=${encodeURIComponent(options.fromDate)}`;
  }
  if (options.toDate) {
    url += `&endDate=${encodeURIComponent(options.toDate)}`;
  }
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await getData(url, headers);
      if (response.status == 200) {
        dispatch(GetCurrentTableInfoSuccessAction(response.data.data));
        dispatch(ApiCallSuccessAction(handleApiSuccess('')));
        return {
          status: true,
          data: response.data.data,
        };
      } else {
        dispatch(ApiCallFailedAction(handleApiError('Error while fetching')));
        return {
          status: false,
          data: null,
        };
      }
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function GetCoursesByStatusAction(status, metaData) {
  if (!status) return;
  const url = `${getAPIEndpoints().getDeliveriesByStatus}/${status}/courses`;
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await getData(url, headers);
      dispatch(GetCoursesByStatusSuccessAction(response.data.data));
      dispatch(ApiCallSuccessAction(handleApiSuccess('')));
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function MoveDeliveriesToBeDispatchedAction(packageIds, options = {}, metaData) {
  const url = getAPIEndpoints().moveDeliveryToBeDispatched;
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await putData(url, { packageIds }, headers);
      const movedCount = response.data?.data?.moved?.length ?? 0;
      response.data.data.message = `${movedCount} order(s) moved to 'To be Dispatched' successfully!`;
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      dispatch(
        GetDeliveriesListByStatusAction({
          status: DeliveryPackageStatusEnum.NewOrder,
          page: options.page ?? 0,
          size: options.size ?? 10,
        })
      );
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function GetSelectedItemDetailsAction(id, headers, metaData) {
  const url = getAPIEndpoints().getDeliveryPackageById.replace('{packageId}', id);
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await getData(url, headers);
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function UpdateAddressAction(payload, headers, metaData) {
  const url = getAPIEndpoints().updateDeliveryAddress;
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await putData(url, payload, headers);
      response.data.data.message = 'The address changes have been saved successfully!';
      dispatch(UpdateAddressSuccessAction(response.data.data));
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function ProcessOrderAction(payload, message, headers, metaData, status) {
  const url = getAPIEndpoints().processDeliveryPackage;
  return async (dispatch, getState) => {
    try {
      dispatch(ApiCallInProgress());
      const response = await putData(url, payload, headers);
      dispatch(ProcessOrderSuccessAction(response.data.data));
      if (message) {
        response.data.data.message = message;
      }
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      const { pageable } = getState().deliveryState.paginatedResponse;
      dispatch(
        GetDeliveriesListByStatusAction({
          status: status,
          page: pageable?.pageNumber,
          size: pageable?.pageSize,
        })
      );
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function CancelOrderAction(payload, headers, metaData, status) {
  const url = getAPIEndpoints().processDeliveryPackage;
  return async (dispatch, getState) => {
    try {
      dispatch(ApiCallInProgress());
      const response = await putData(url, payload, headers);
      dispatch(CancelOrderSuccessAction(response.data.data));
      response.data.data.message = 'The Order has been cancelled successfully!';
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      const { pageable } = getState().deliveryState.paginatedResponse;
      dispatch(
        GetDeliveriesListByStatusAction({
          status: status,
          page: pageable?.pageNumber,
          size: pageable?.pageSize,
        })
      );
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function UpdateOrderAction(payload, message, headers, metaData) {
  const url = getAPIEndpoints().updateDeliveryPackage;
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await putData(url, payload, headers);
      dispatch(UpdateOrderSuccessAction(response.data.data));
      if (message) {
        response.data.data.message = message;
      }
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return {
        status: true,
        response: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function SendHandDeliveryOtpAction(deliveryPackageId, metaData) {
  const url = getAPIEndpoints().sendHandDeliveryOtp.replace(
    '{deliveryPackageId}',
    deliveryPackageId
  );
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(url, {}, headers);
      response.data.data.message = "OTP has been sent to the student's registered mobile number!";
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function ResendHandDeliveryOtpAction(deliveryPackageId, metaData) {
  const url = getAPIEndpoints().resendHandDeliveryOtp.replace(
    '{deliveryPackageId}',
    deliveryPackageId
  );
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(url, {}, headers);
      response.data.data.message =
        "A new OTP has been sent to the student's registered mobile number!";
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function VerifyHandDeliveryOtpAction(deliveryPackageId, otp, metaData) {
  const url = getAPIEndpoints().verifyHandDeliveryOtp;
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(url, { deliveryPackageId, otp }, headers);
      response.data.data.message = 'OTP verified successfully!';
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
      };
    }
  };
}

export function GetAllCataloguesDeliveryAction(url, payload, headers, metaData) {
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await getData(url, payload, headers);
      dispatch(GetAllCataloguesDeliverySuccessAction(response.data));
      dispatch(ApiCallSuccessAction(handleApiSuccess(response, metaData)));
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function DownloadTrackingTemplateAction(metaData) {
  const url = getAPIEndpoints().downloadTrackingTemplate;
  const headers = generateUserInfoHeader();
  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await getData(url, headers, { responseType: 'blob' });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'Tracking_ID_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      dispatch(
        ApiCallSuccessAction(
          handleApiSuccess({ data: { message: 'Template downloaded successfully!' } })
        )
      );
      return true;
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return false;
    }
  };
}

export function BulkAssignTrackingIdsAction(
  deliveryPackageIds,
  { serviceProvider, status },
  metaData
) {
  const url = getAPIEndpoints().bulkAssignTrackingIds;
  const headers = generateUserInfoHeader();
  return async (dispatch, getState) => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(
        url,
        // dispatchType is decided later in the process-order drawer, not at assignment time
        { deliveryPackageIds, deliveryMode: 'COURIER', dispatchType: 'COMPLETE', serviceProvider },
        headers
      );
      const { assigned = [], skipped = [] } = response.data.data;
      dispatch(BulkAssignTrackingSuccessAction({ assigned, skipped }));
      response.data.data.message = `${assigned.length} tracking ID(s) assigned successfully!`;
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      const { pageable } = getState().deliveryState.paginatedResponse;
      dispatch(
        GetDeliveriesListByStatusAction({
          status,
          page: pageable?.pageNumber,
          size: pageable?.pageSize,
        })
      );
      return { status: true, assigned, skipped };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return { status: false, error: err?.response?.data || err };
    }
  };
}

export function BulkClearTrackingIdsAction(deliveryPackageIds, status, metaData) {
  const url = getAPIEndpoints().bulkClearTrackingIds;
  const headers = generateUserInfoHeader();
  return async (dispatch, getState) => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(url, { deliveryPackageIds }, headers);
      response.data.data.message = 'Tracking ID(s) cleared successfully!';
      dispatch(ApiCallSuccessAction(handleApiSuccess(response.data, metaData)));
      const { pageable } = getState().deliveryState.paginatedResponse;
      dispatch(
        GetDeliveriesListByStatusAction({
          status,
          page: pageable?.pageNumber,
          size: pageable?.pageSize,
        })
      );
      return { status: true };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return { status: false, error: err?.response?.data || err };
    }
  };
}

export function UploadTrackingIdsAction(file, metaData) {
  const url = getAPIEndpoints().uploadTrackingIds;
  const headers = {
    ...generateUserInfoHeader(),
    'Content-Type': 'multipart/form-data',
  };
  const formData = new FormData();
  formData.append('file', file);

  return async dispatch => {
    try {
      dispatch(ApiCallInProgress());
      const response = await postData(url, formData, headers);
      dispatch(
        ApiCallSuccessAction({
          showBanner: false,
          message: '',
        })
      );
      return {
        status: true,
        data: response.data.data,
      };
    } catch (err) {
      dispatch(ApiCallFailedAction(handleApiError(err, metaData)));
      return {
        status: false,
        error: err,
      };
    }
  };
}
